import {
  Button,
  FormGroup,
  Input,
  Modal,
  Textarea,
} from '@orobix/orobix-fe-components'
import { Driver } from 'driver.js'
import { Formik } from 'formik'
import i18n from 'i18next'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import * as Yup from 'yup'

import DemoBadge from '../../components/DemoBadge/DemoBadge'
import { useDispatch } from '../../store/store'
import { createProduct, updateProduct } from '../../store/storeActions'
import { ProductType } from '../../types/productType'
import { unique } from '../../utils/forms'
import {
  ANOMALY,
  CLASSIFICATION,
  isProductDemo,
  isProductEnabledOrDemo,
  PATCH,
  SEGMENTATION,
} from '../../utils/productTypes'
import ClassesField from './ClassesField'
import useStyles from './ProductsCreateUpdate.styles'

interface RouteParams {
  projectId: string
}

export interface FormValues {
  name: string
  classes: string[]
  type: ProductType
  description?: string
}

interface ProductsCreateUpdateProps {
  setModal: (value: boolean) => void
  actionType: 'create' | 'update'
  initialValues?: FormValues
  productId?: number
  setLastDescriptionUpdated?: React.Dispatch<
    React.SetStateAction<{
      productId: number
      description: string
    }>
  >
  driverObj?: Driver
}

// hardcoded class name for 'Anomaly detection'
const ANOMALY_CLASS = 'anomaly'
const NORMAL_CLASS = 'normal'

const productCreateValidationSchema = Yup.object({
  name: Yup.string().required(i18n.t('This field is required')),
  classes: Yup.array()
    .of(
      Yup.string()
        .matches(/^[aA-zZ0-9-_\s]+$/, 'No special characters are allowed')
        .max(
          20,
          i18n.t("A class can't be longer than {{ count }} characters", {
            count: 20,
          })
        )
        .required(i18n.t('This field is required'))
    )
    .test(
      'unique',
      i18n.t('Classes need to be unique, please remove duplicates'),
      (values) => unique(values)
    ),
})

const productUpdateValidationSchema = Yup.object({
  name: Yup.string().required(i18n.t('This field is required')),
})

const ProductsCreateUpdate = ({
  setModal,
  actionType,
  initialValues = {
    name: '',
    classes: [],
    type: 'classification',
    description: '',
  },
  productId,
  setLastDescriptionUpdated,
  driverObj,
}: ProductsCreateUpdateProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()
  const { projectId } = useParams<RouteParams>()

  useEffect(() => {
    if (driverObj?.isActive()) {
      if (driverObj.getActiveIndex() === 1) {
        driverObj.moveNext()
      }
      if (driverObj.isLastStep()) {
        driverObj.movePrevious()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={
        actionType === 'create'
          ? productCreateValidationSchema
          : productUpdateValidationSchema
      }
      onSubmit={(values, { setSubmitting }) => {
        if (actionType === 'update' && productId) {
          updateProduct(
            dispatch,
            productId,
            values.name,
            values.description || '',
            setSubmitting,
            setModal,
            setLastDescriptionUpdated
          )
        } else if (values.classes) {
          createProduct(
            dispatch,
            Number(projectId),
            values.name,
            values.description || '',
            values.type,
            values.classes.map((c) => c.trim()).join(),
            setSubmitting,
            setModal
          )
        }
        if (driverObj?.isActive()) driverObj.moveNext()
      }}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setFieldError,
        setFieldTouched,
      }) => (
        <form onSubmit={handleSubmit}>
          <Modal
            size="md"
            onCloseRequest={() => {
              setModal(false)
            }}
            title={
              actionType === 'create'
                ? t('Create new product')
                : t('Product settings')
            }
            footer={
              <div id="productButtonsFooterId">
                <Button
                  label={t('Close')}
                  type="default"
                  buttonType="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    setModal(false)
                    if (driverObj?.isActive()) driverObj.moveNext()
                  }}
                />
                <Button
                  label={actionType === 'create' ? t('Create') : t('Edit')}
                  type="action"
                  disabled={isSubmitting}
                />
              </div>
            }
          >
            <div id="productNameId">
              <Input
                name="name"
                id="name"
                label={t('Name')}
                type="text"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('name', e.target.value)
                }
                onBlur={handleBlur}
                value={values.name}
                error={!!(errors.name && touched.name)}
                errorMessage={errors.name}
                disabled={isSubmitting}
              />
            </div>

            <div id="productDescriptionId">
              <Textarea
                name="description"
                id="description"
                label={t('Description')}
                type="text"
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFieldValue('description', e.target.value)
                }
                onBlur={handleBlur}
                value={values.description}
                error={!!(errors.description && touched.description)}
                errorMessage={errors.description}
                disabled={isSubmitting}
              />
            </div>

            <div id="productTypeAndClassesId">
              <FormGroup
                data-testid="fieldsetType"
                label={t('Type')}
                disabled={isSubmitting || actionType !== 'create'}
              >
                {isProductEnabledOrDemo(CLASSIFICATION) && (
                  <div className={classes.productTypeRadio}>
                    <Input
                      data-testid="inputTypeClassification"
                      label={t('Classification')}
                      type="radio"
                      name="type"
                      value="classification"
                      id="classification"
                      checked={values.type === 'classification'}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue('type', e.target.value)
                        setFieldValue('classes', [''])
                        setFieldError('classes', '')
                        setFieldTouched('classes', false)
                      }}
                      inline
                      disabled={isSubmitting || actionType !== 'create'}
                    />

                    {isProductDemo(CLASSIFICATION) && (
                      <div className={classes.demoBadge}>
                        <DemoBadge />
                      </div>
                    )}
                  </div>
                )}

                {isProductEnabledOrDemo(SEGMENTATION) && (
                  <div className={classes.productTypeRadio}>
                    <Input
                      data-testid="inputTypeSegmentation"
                      label={t('Segmentation')}
                      type="radio"
                      name="type"
                      value="segmentation"
                      id="segmentation"
                      checked={values.type === 'segmentation'}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue('type', e.target.value)
                        setFieldValue('classes', [''])
                        setFieldError('classes', '')
                        setFieldTouched('classes', false)
                      }}
                      inline
                      disabled={isSubmitting || actionType !== 'create'}
                    />

                    {isProductDemo(SEGMENTATION) && (
                      <div className={classes.demoBadge}>
                        <DemoBadge />
                      </div>
                    )}
                  </div>
                )}

                {isProductEnabledOrDemo(PATCH) && (
                  <div className={classes.productTypeRadio}>
                    <Input
                      data-testid="inputTypePatch"
                      label={t('Patch')}
                      type="radio"
                      name="type"
                      value="classification_patch"
                      id="classification_patch"
                      checked={values.type === 'classification_patch'}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue('type', e.target.value)
                        setFieldValue('classes', [''])
                        setFieldError('classes', '')
                        setFieldTouched('classes', false)
                      }}
                      inline
                      disabled={isSubmitting || actionType !== 'create'}
                    />

                    {isProductDemo(PATCH) && (
                      <div className={classes.demoBadge}>
                        <DemoBadge />
                      </div>
                    )}
                  </div>
                )}

                {isProductEnabledOrDemo(ANOMALY) && (
                  <div className={classes.productTypeRadio}>
                    <Input
                      data-testid="inputTypeAnomaly"
                      label="Anomaly detection"
                      type="radio"
                      name="type"
                      value="anomaly"
                      id="anomaly"
                      checked={values.type === 'anomaly'}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue('type', e.target.value)
                        setFieldValue('classes', [ANOMALY_CLASS, NORMAL_CLASS])
                        setFieldError('classes', '')
                        setFieldTouched('classes', false)
                      }}
                      inline
                      disabled={isSubmitting || actionType !== 'create'}
                    />
                    {isProductDemo(ANOMALY) && (
                      <div className={classes.demoBadge}>
                        <DemoBadge />
                      </div>
                    )}
                  </div>
                )}
              </FormGroup>

              {(() =>
                ({
                  anomaly: (
                    <ClassesField
                      fieldName="classes"
                      actionType={actionType}
                      min={2}
                      max={2}
                      disabled
                    />
                  ),

                  classification: (
                    <ClassesField
                      fieldName="classes"
                      actionType={actionType}
                      min={2}
                      max={10}
                      productType={values.type}
                    />
                  ),

                  segmentation: (
                    <ClassesField
                      fieldName="classes"
                      actionType={actionType}
                      min={1}
                      max={10}
                      productType={values.type}
                    />
                  ),

                  classification_patch: (
                    <ClassesField
                      fieldName="classes"
                      actionType={actionType}
                      min={1}
                      max={10}
                      productType={values.type}
                    />
                  ),
                }[values.type]))()}
            </div>
          </Modal>
        </form>
      )}
    </Formik>
  )
}

export default ProductsCreateUpdate
